import { useState, useEffect, useRef, useCallback } from 'react'
import { fetchApi } from '@/lib/api'
import { createReleaseSocket, type ReleaseEvent } from '@/lib/releaseWs'
import { MarkdownRenderer } from '@/components/MarkdownRenderer'
import {
  Rocket, Play, Eye, XCircle, History,
  CheckCircle, AlertCircle, Loader2, MinusCircle,
  Clock, Bot, ChevronDown, ChevronRight,
  ExternalLink,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { formatDateTime } from '@/lib/date'

type ReleaseConfig = {
  targets: string[]
  deploy_workflows: Record<string, string>
  steps: string[]
}

type StepState = {
  status: string
  detail: string
  error: string
  pr_number: number | null
  pr_url: string | null
  merge_sha: string | null
  diagnosis: string | null
}

type ReleaseDetail = {
  id: number
  status: string
  dry_run: boolean
  repos: string[]
  started_by: string
  error_message: string | null
  created_at: string
  completed_at: string | null
  step_matrix: Record<string, Record<string, StepState>>
}

type ReleaseSummary = {
  id: number
  status: string
  dry_run: boolean
  repos: string[]
  started_by: string
  error_message: string | null
  created_at: string
  completed_at: string | null
}

const STEP_LABELS: Record<string, string> = {
  diff_check: 'Diff',
  pr_create: 'PR',
  ci_wait: 'CI',
  conflict_check: 'Conflict',
  merge: 'Merge',
  deploy_wait: 'Deploy',
}

const REPO_SHORT: Record<string, string> = {
  'twp-cdk': 'cdk',
  'twp-agent-egress-proxy': 'egress',
  'twp-backend': 'backend',
  'twp-agent-images': 'images',
  'twp-orchestrator': 'orchestrator',
  'twp-front': 'front',
  'twp-public-docs': 'public-docs',
  'twp-mcp': 'mcp',
}

function StepIcon({ status }: { status: string }) {
  switch (status) {
    case 'success':
      return <CheckCircle size={16} className="text-green-400" />
    case 'running':
      return <Loader2 size={16} className="text-blue-400 animate-spin" />
    case 'failed':
      return <AlertCircle size={16} className="text-red-400" />
    case 'skipped':
      return <MinusCircle size={16} className="text-yellow-500/60" />
    case 'pending':
    default:
      return <Clock size={16} className="text-[var(--color-text-muted)]/40" />
  }
}

function stepBgClass(status: string): string {
  switch (status) {
    case 'success':
      return 'bg-green-500/10'
    case 'running':
      return 'bg-blue-500/10'
    case 'failed':
      return 'bg-red-500/10'
    case 'skipped':
      return 'bg-yellow-500/5'
    default:
      return ''
  }
}

function releaseStatusBadge(status: string) {
  const map: Record<string, { label: string; cls: string }> = {
    pending: { label: 'Pending', cls: 'bg-gray-500/20 text-gray-400' },
    running: { label: 'Running', cls: 'bg-blue-500/20 text-blue-400' },
    completed: { label: 'Completed', cls: 'bg-green-500/20 text-green-400' },
    failed: { label: 'Failed', cls: 'bg-red-500/20 text-red-400' },
    cancelled: { label: 'Cancelled', cls: 'bg-yellow-500/20 text-yellow-400' },
    interrupted: { label: 'Interrupted', cls: 'bg-orange-500/20 text-orange-400' },
  }
  const s = map[status] ?? map.pending
  return (
    <span className={cn('px-2 py-0.5 rounded text-xs font-medium', s.cls)}>
      {s.label}
    </span>
  )
}

export function Release() {
  const [config, setConfig] = useState<ReleaseConfig | null>(null)
  const [selectedRepos, setSelectedRepos] = useState<Set<string>>(new Set())
  const [currentRelease, setCurrentRelease] = useState<ReleaseDetail | null>(null)
  const [stepMatrix, setStepMatrix] = useState<Record<string, Record<string, StepState>>>({})
  const [releaseStatus, setReleaseStatus] = useState<string>('')
  const [diagnosis, setDiagnosis] = useState<Record<string, string>>({})
  const [diagnosisExpanded, setDiagnosisExpanded] = useState<string | null>(null)
  const [history, setHistory] = useState<ReleaseSummary[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const socketRef = useRef<ReturnType<typeof createReleaseSocket> | null>(null)
  const releaseIdRef = useRef<number | null>(null)

  // Load config on mount
  useEffect(() => {
    fetchApi<ReleaseConfig>('/release/config')
      .then((c) => {
        setConfig(c)
        setSelectedRepos(new Set(c.targets))
      })
      .catch((e) => setError(e.message))
  }, [])

  // REST polling (primary update mechanism - works across uvicorn workers)
  const startPolling = useCallback((releaseId: number) => {
    releaseIdRef.current = releaseId
    if (pollingRef.current) clearInterval(pollingRef.current)

    const poll = async () => {
      if (!releaseIdRef.current) return
      try {
        const detail = await fetchApi<ReleaseDetail>(`/release/${releaseIdRef.current}`)
        setStepMatrix(detail.step_matrix)
        setReleaseStatus(detail.status)

        // Extract diagnosis from step data
        const diagMap: Record<string, string> = {}
        for (const [repo, steps] of Object.entries(detail.step_matrix)) {
          for (const step of Object.values(steps)) {
            if (step.diagnosis) {
              diagMap[repo] = step.diagnosis
            }
          }
        }
        if (Object.keys(diagMap).length > 0) {
          setDiagnosis(diagMap)
        }

        // Stop polling when release is done
        if (detail.status === 'completed' || detail.status === 'failed' || detail.status === 'cancelled') {
          setLoading(false)
          stopPolling()
        }
      } catch {
        // Ignore polling errors
      }
    }

    pollingRef.current = setInterval(poll, 3000)
    // Also poll immediately
    poll()
  }, [])

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
    releaseIdRef.current = null
  }, [])

  // WebSocket connection (bonus: real-time updates when same worker)
  const connectWs = useCallback(() => {
    if (socketRef.current) return

    const socket = createReleaseSocket(
      (event: ReleaseEvent) => {
        if (event.type === 'step_update' && event.repo && event.step) {
          setStepMatrix((prev) => ({
            ...prev,
            [event.repo!]: {
              ...prev[event.repo!],
              [event.step!]: {
                ...prev[event.repo!]?.[event.step!],
                status: event.status ?? 'pending',
                detail: event.detail ?? '',
                error: event.error ?? '',
              },
            },
          }))
        } else if (event.type === 'release_status') {
          setReleaseStatus(event.status ?? '')
          if (event.status === 'completed' || event.status === 'failed' || event.status === 'cancelled') {
            setLoading(false)
            stopPolling()
          }
        } else if (event.type === 'diagnosis_stream' && event.repo) {
          setDiagnosis((prev) => ({
            ...prev,
            [event.repo!]: (prev[event.repo!] ?? '') + (event.content ?? ''),
          }))
          setDiagnosisExpanded(event.repo!)
        } else if (event.type === 'diagnosis_done' && event.repo) {
          setDiagnosis((prev) => ({
            ...prev,
            [event.repo!]: event.diagnosis ?? prev[event.repo!] ?? '',
          }))
        }
      },
      () => {
        socketRef.current = null
      },
    )
    socketRef.current = socket
  }, [stopPolling])

  useEffect(() => {
    return () => {
      socketRef.current?.close()
      stopPolling()
    }
  }, [stopPolling])

  const toggleRepo = (repo: string) => {
    setSelectedRepos((prev) => {
      const next = new Set(prev)
      if (next.has(repo)) next.delete(repo)
      else next.add(repo)
      return next
    })
  }

  const selectAll = () => {
    if (config) setSelectedRepos(new Set(config.targets))
  }

  const selectNone = () => {
    setSelectedRepos(new Set())
  }

  const startRelease = async (dryRun: boolean) => {
    if (selectedRepos.size === 0) {
      setError('Select at least one repository')
      return
    }

    if (!dryRun) {
      const ok = window.confirm(
        '本番環境へのリリースを実行しますか？\n\n' +
        `対象: ${Array.from(selectedRepos).join(', ')}\n\n` +
        'この操作は取り消せません。'
      )
      if (!ok) return
    }

    setError('')
    setLoading(true)
    setDiagnosis({})
    setDiagnosisExpanded(null)

    try {
      const result = await fetchApi<{ status: string; release_id?: number; message?: string }>(
        '/release/start',
        {
          method: 'POST',
          body: JSON.stringify({
            dry_run: dryRun,
            repos: Array.from(selectedRepos),
          }),
        },
      )

      if (result.status === 'error') {
        setError(result.message ?? 'Unknown error')
        setLoading(false)
        return
      }

      // Load initial detail and start polling + WebSocket
      if (result.release_id) {
        const detail = await fetchApi<ReleaseDetail>(`/release/${result.release_id}`)
        setCurrentRelease(detail)
        setStepMatrix(detail.step_matrix)
        setReleaseStatus(detail.status)
        startPolling(result.release_id)
        connectWs()
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to start release')
      setLoading(false)
    }
  }

  const cancelRelease = async () => {
    if (!currentRelease) return
    try {
      await fetchApi(`/release/${currentRelease.id}/cancel`, { method: 'POST' })
    } catch {
      // ignore
    }
  }

  const resumeRelease = async () => {
    if (!currentRelease) return
    const ok = window.confirm(
      `Release #${currentRelease.id} を再開しますか？\n中断したステップから続行します。`
    )
    if (!ok) return

    setError('')
    setLoading(true)
    try {
      const result = await fetchApi<{ status: string; release_id?: number; message?: string }>(
        `/release/${currentRelease.id}/resume`,
        { method: 'POST' },
      )
      if (result.status === 'error') {
        setError(result.message ?? 'Unknown error')
        setLoading(false)
        return
      }
      setReleaseStatus('running')
      startPolling(currentRelease.id)
      connectWs()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to resume release')
      setLoading(false)
    }
  }

  const loadHistory = async () => {
    try {
      const list = await fetchApi<ReleaseSummary[]>('/release/list')
      setHistory(list)
      setShowHistory(true)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load history')
    }
  }

  const loadRelease = async (id: number) => {
    try {
      const detail = await fetchApi<ReleaseDetail>(`/release/${id}`)
      setCurrentRelease(detail)
      setStepMatrix(detail.step_matrix)
      setReleaseStatus(detail.status)
      setShowHistory(false)
      // Load diagnosis from stored data
      const diagMap: Record<string, string> = {}
      for (const [repo, steps] of Object.entries(detail.step_matrix)) {
        for (const step of Object.values(steps)) {
          if (step.diagnosis) {
            diagMap[repo] = step.diagnosis
          }
        }
      }
      setDiagnosis(diagMap)

      // Start polling + WS if release is still running
      if (detail.status === 'running') {
        startPolling(detail.id)
        connectWs()
        setLoading(true)
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load release')
    }
  }

  const triggerDiagnosis = async (repo: string) => {
    if (!currentRelease) return
    try {
      await fetchApi(`/release/${currentRelease.id}/diagnose/${repo}`, { method: 'POST' })
      connectWs()
    } catch {
      // ignore
    }
  }

  const repos = currentRelease?.repos ?? Array.from(selectedRepos)
  const steps = config?.steps ?? []
  const isRunning = releaseStatus === 'running' || loading

  return (
    <div className="p-4 h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 shrink-0 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Rocket size={22} className="text-[var(--color-primary)]" />
          <h2 className="text-xl font-bold">Production Release</h2>
          {currentRelease && releaseStatusBadge(releaseStatus || currentRelease.status)}
        </div>
        <div className="flex items-center gap-2">
          {isRunning && currentRelease && (
            <button
              onClick={cancelRelease}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
            >
              <XCircle size={14} />
              Cancel
            </button>
          )}
          {releaseStatus === 'interrupted' && currentRelease && (
            <button
              onClick={resumeRelease}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 transition-colors"
            >
              <Play size={14} />
              Resume
            </button>
          )}
          <button
            onClick={() => startRelease(true)}
            disabled={isRunning || selectedRepos.size === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] text-[var(--color-text)] transition-colors disabled:opacity-40"
          >
            <Eye size={14} />
            Dry-run
          </button>
          <button
            onClick={() => startRelease(false)}
            disabled={isRunning || selectedRepos.size === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-[var(--color-primary)] hover:opacity-90 text-white transition-colors disabled:opacity-40"
          >
            <Play size={14} />
            Release
          </button>
          <button
            onClick={loadHistory}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] text-[var(--color-text-muted)] transition-colors"
          >
            <History size={14} />
            History
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm shrink-0">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline text-xs">dismiss</button>
        </div>
      )}

      {/* History modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowHistory(false)}>
          <div
            className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-4 max-w-2xl w-full mx-4 max-h-[80vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-3">Release History</h3>
            {history.length === 0 ? (
              <p className="text-[var(--color-text-muted)] text-sm">No releases yet.</p>
            ) : (
              <div className="space-y-2">
                {history.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => loadRelease(r.id)}
                    className="w-full text-left p-3 rounded bg-[var(--color-bg)] hover:bg-[var(--color-bg-hover)] border border-[var(--color-border)] transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-mono">Release #{r.id}</span>
                      {releaseStatusBadge(r.status)}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-[var(--color-text-muted)]">
                      <span>{r.started_by}</span>
                      <span>{formatDateTime(r.created_at)}</span>
                      {r.dry_run && <span className="text-yellow-500">(dry-run)</span>}
                      <span>{r.repos.length} repos</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto">
        {/* Repo selection (only when no active release) */}
        {!currentRelease && config && (
          <div className="mb-4 p-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Target Repositories</span>
              <div className="flex gap-2 text-xs">
                <button onClick={selectAll} className="text-[var(--color-primary)] hover:underline">All</button>
                <button onClick={selectNone} className="text-[var(--color-text-muted)] hover:underline">None</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {config.targets.map((repo) => (
                <label
                  key={repo}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded text-xs border cursor-pointer transition-colors',
                    selectedRepos.has(repo)
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                      : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-text-muted)]',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selectedRepos.has(repo)}
                    onChange={() => toggleRepo(repo)}
                    className="sr-only"
                  />
                  {repo}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Pipeline grid */}
        {(currentRelease || Object.keys(stepMatrix).length > 0) && (
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] overflow-hidden">
            {currentRelease && (
              <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--color-border)] bg-[var(--color-bg)]/50">
                <div className="flex items-center gap-3 text-sm">
                  <span className="font-mono text-[var(--color-text-muted)]">#{currentRelease.id}</span>
                  <span>{currentRelease.started_by}</span>
                  <span className="text-[var(--color-text-muted)]">
                    {formatDateTime(currentRelease.created_at)}
                  </span>
                  {currentRelease.dry_run && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] bg-yellow-500/20 text-yellow-400">DRY-RUN</span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setCurrentRelease(null)
                    setStepMatrix({})
                    setReleaseStatus('')
                    setDiagnosis({})
                    setLoading(false)
                    stopPolling()
                  }}
                  className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                >
                  New Release
                </button>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="text-left px-4 py-2 text-[var(--color-text-muted)] font-medium w-40">
                      Repository
                    </th>
                    {steps.map((step) => (
                      <th
                        key={step}
                        className="text-center px-3 py-2 text-[var(--color-text-muted)] font-medium text-xs"
                      >
                        {STEP_LABELS[step] ?? step}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {repos.map((repo) => {
                    const repoSteps = stepMatrix[repo] ?? {}
                    const hasFailed = Object.values(repoSteps).some((s) => s.status === 'failed')
                    return (
                      <tr key={repo} className="border-b border-[var(--color-border)] last:border-0">
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-xs">{REPO_SHORT[repo] ?? repo}</span>
                            {hasFailed && (
                              <button
                                onClick={() => triggerDiagnosis(repo)}
                                title="Run AI diagnosis"
                                className="text-[var(--color-primary)] hover:text-blue-300"
                              >
                                <Bot size={14} />
                              </button>
                            )}
                          </div>
                          {repoSteps.pr_create?.pr_url && (
                            <a
                              href={repoSteps.pr_create.pr_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] text-[var(--color-primary)] hover:underline flex items-center gap-0.5 mt-0.5"
                            >
                              PR #{repoSteps.pr_create.pr_number}
                              <ExternalLink size={9} />
                            </a>
                          )}
                        </td>
                        {steps.map((step) => {
                          const s = repoSteps[step]
                          const status = s?.status ?? 'pending'
                          return (
                            <td
                              key={step}
                              className={cn('text-center px-3 py-2', stepBgClass(status))}
                              title={s?.detail || s?.error || status}
                            >
                              <div className="flex items-center justify-center gap-1">
                                <StepIcon status={status} />
                                {status === 'failed' && diagnosis[repo] && (
                                  <Bot size={12} className="text-[var(--color-primary)]" />
                                )}
                              </div>
                              {s?.error && status === 'failed' && (
                                <div className="text-[10px] text-red-400/80 mt-0.5 truncate max-w-[100px]" title={s.error}>
                                  {s.error}
                                </div>
                              )}
                              {s?.detail && status !== 'pending' && status !== 'failed' && (
                                <div className="text-[10px] text-[var(--color-text-muted)] mt-0.5 truncate max-w-[100px]">
                                  {s.detail}
                                </div>
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* AI Diagnosis panel */}
        {Object.keys(diagnosis).length > 0 && (
          <div className="mt-4 space-y-2">
            {Object.entries(diagnosis).map(([repo, content]) => (
              <div
                key={repo}
                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] overflow-hidden"
              >
                <button
                  onClick={() => setDiagnosisExpanded(diagnosisExpanded === repo ? null : repo)}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-[var(--color-bg-hover)] transition-colors"
                >
                  {diagnosisExpanded === repo ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <Bot size={14} className="text-[var(--color-primary)]" />
                  <span className="font-medium">AI Diagnosis</span>
                  <span className="text-[var(--color-text-muted)] font-mono text-xs">{repo}</span>
                </button>
                {diagnosisExpanded === repo && (
                  <div className="px-4 pb-4 border-t border-[var(--color-border)]">
                    <div className="mt-3">
                      <MarkdownRenderer
                        content={content}
                        isStreaming={releaseStatus === 'running'}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!currentRelease && Object.keys(stepMatrix).length === 0 && !showHistory && (
          <div className="text-center py-16 text-[var(--color-text-muted)]">
            <Rocket size={48} className="mx-auto mb-3 opacity-20" />
            <p className="text-sm font-medium">Production Release Pipeline</p>
            <p className="text-xs mt-1">
              develop → main の差分確認、PR作成、CI監視、マージ、デプロイを自動実行
            </p>
            <p className="text-xs mt-3 text-[var(--color-text-muted)]/60">
              Dry-run で差分プレビュー → Release で本番デプロイ
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
